import React from 'react'
import { useNavigate } from 'react-router'
import { post } from '@mobi/account/Utils'
import type { MfaUpdateAuthRequestData } from '@mobi/api-types'
import { AccountHeader } from '@mobi/component-library/Areas/Account'
import { Button } from '@mobi/component-library/Base/Buttons'
import { FeatureIcon, Icon } from '@mobi/component-library/Assets/Icon'
import { colors } from '@mobi/component-library/Foundations'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { isReactNativeApp, isReactNativeIos, sendToNative } from '@mobi/web-native-comms/web'
import { useBiometricsStatus } from '../../Verification/Biometrics/Hooks/useBiometricsStatus'
import { Toggle } from '@mobi/component-library/Selection&Input/Toggle'
import {
  Selector,
  SelectorHeading,
  MessageContainer,
  ToggleGroupItem,
  ChildrenContainer,
} from './SecuritySettings.styled'
import { routes } from '../Utils'
import { useSelector } from 'react-redux'
import { getSignUpState } from '../Store'
import { useBetAccount } from '@mobi/account/Hooks'
import { signupTracker } from '@mobi/account/Common/Analytics'
import { incrementQuickCounter } from '@mobi/utils/logging/quickCounter'
import { routes as verificationAccountRoutes } from '../../VerificationAccount/Utils/routes'

type Props = {
  password: string
}

export const SecuritySettings = ({ password }: Props) => {
  const [isBiometricsToggleOn, setIsBiometricsToggleOn] = React.useState(true)
  const [isMfaToggleOn, setIsMfaToggleOn] = React.useState(true)
  const signUpState = useSelector(getSignUpState)
  const { data } = useBetAccount()

  const navigate = useNavigate()
  const { biometricsStatus } = useBiometricsStatus()

  const biometricsType = biometricsStatus?.biometricsType
  const isFaceId = biometricsType == 'FACIAL_RECOGNITION' && isReactNativeIos
  const biometricsPromptText = isFaceId ? 'Face ID' : 'PIN / Biometrics'
  const isBiometricsAvailable = isReactNativeApp && data?.BetAccountNumber && password !== ''

  const areVerificationAccountsActive = signUpState.isVerificationAccount
  const nextStep = areVerificationAccountsActive
    ? verificationAccountRoutes.VerificationAccountHome
    : routes.depositLimit

  React.useEffect(() => {
    // clear any biometrics password that might be saved from a previously logged in user
    if (data?.BetAccountNumber) {
      sendToNative('REMOVE_BIOMETRICS_PASSWORD')
    }
  }, [data?.BetAccountNumber])

  React.useEffect(() => {
    if (!isBiometricsAvailable && !signUpState.mobileNumberVerified) {
      navigate(nextStep)
    }
  }, [isBiometricsAvailable, signUpState.mobileNumberVerified, navigate, nextStep])

  if (!isBiometricsAvailable && !signUpState.mobileNumberVerified) {
    return null
  }

  return (
    <SignUpPageStyled>
      <AccountHeader
        alignItems='start'
        title='Protect your TABtouch account'
        description='Adding extra security will ensure you are the only one who can access your TABtouch account.'
        style={{ marginBottom: '0rem' }}
      />
      <Content>
        <Selector>
          <SelectorHeading>
            <Icon name='SolidLock01' color={colors.studio[500]} size='2.4rem' />
            <MessageContainer>
              <div>Security</div>
              <div>Make your account more secure</div>
            </MessageContainer>
          </SelectorHeading>
          {isBiometricsAvailable && (
            <ToggleGroupItem id='biometrics'>
              <FeatureIcon type='custom' size='md'>
                <Icon name='LineFaceId' color={colors.lavender['600']} size='1.6rem' />
              </FeatureIcon>
              <ChildrenContainer>{biometricsPromptText}</ChildrenContainer>
              <Toggle
                id='biometrics-toggle'
                isOn={isBiometricsToggleOn}
                onToggle={function (): void {
                  setIsBiometricsToggleOn(!isBiometricsToggleOn)
                }}
              />
            </ToggleGroupItem>
          )}
          {signUpState.mobileNumberVerified && (
            <ToggleGroupItem id='mfa'>
              <FeatureIcon type='custom' size='md'>
                <Icon name='LineShield01' color={colors.lavender['600']} size='1.6rem' />
              </FeatureIcon>
              <ChildrenContainer>Multi-Factor Authentication (MFA)</ChildrenContainer>
              <Toggle
                id='mfa-toggle'
                isOn={isMfaToggleOn}
                onToggle={function (): void {
                  setIsMfaToggleOn(!isMfaToggleOn)
                }}
              />
            </ToggleGroupItem>
          )}
        </Selector>
      </Content>
      <Footer>
        <Button
          color='primary'
          isFullWidth
          size='md'
          onClick={async () => {
            if (isBiometricsAvailable && isBiometricsToggleOn) {
              sendToNative('REQUEST_TOGGLE_BIOMETRICS', {
                accountNumber: data?.BetAccountNumber,
                password: password,
                doNotReauthenticate: true,
              })
            }

            if (signUpState.mobileNumberVerified && isMfaToggleOn) {
              // Enable MFA by setting SMS as the preferred factor. The customer's
              // mobile number has already been verified earlier in the sign-up flow.
              post<MfaUpdateAuthRequestData>('/api/mfa/update-auth-factor', {
                preferredFactor: 'SMS',
              })
            }

            signupTracker({
              interaction_type: 'Account Protection',
              bioState: isBiometricsAvailable ? isBiometricsToggleOn : undefined,
              mfaState: signUpState.mobileNumberVerified ? isMfaToggleOn : undefined,
            })
            incrementQuickCounter(
              `mfa-in-signup-toggle-${signUpState.mobileNumberVerified ? isMfaToggleOn : undefined}`.toLocaleLowerCase()
            )

            navigate(nextStep)
          }}
        >
          Continue
        </Button>
        <Button
          color='tertiary_color'
          isFullWidth
          size='md'
          onClick={() => {
            signupTracker({
              interaction_type: 'Account Protection Skipped',
            })
            navigate(nextStep)
          }}
        >
          Skip for now
        </Button>
      </Footer>
    </SignUpPageStyled>
  )
}
